import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import Today from '../components/Today';
import Growth from '../components/Growth';
import System from '../components/System';

export default function Dashboard({ session }) {
  const [active, setActive]       = useState('today');
  const [alerts, setAlerts]       = useState([]);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  // ── Load alerts ──────────────────────────────────────────────────────────
  // WHY: Alerts are checked at the dashboard level so the badge and banner
  //      can appear across all tabs regardless of which tab is active.
  const loadAlerts = useCallback(async () => {
    const found = [];

    // Check Railway
    try {
      const r = await fetch('https://visionry-api-production.up.railway.app/health');
      if (!r.ok) found.push({ type: 'critical', msg: 'Railway API is offline' });
    } catch {
      found.push({ type: 'critical', msg: 'Railway API is offline' });
    }

    // Check Supabase
    try {
      const { error } = await supabase.from('instructors').select('instructor_key').limit(1);
      if (error) found.push({ type: 'critical', msg: 'Supabase is unreachable' });
    } catch {
      found.push({ type: 'critical', msg: 'Supabase is unreachable' });
    }

    // Bookings missing calendar events
    const { data: missingCal } = await supabase
      .from('bookings')
      .select('booking_id')
      .eq('status', 'booked')
      .is('gcal_event_id', null);
    if (missingCal?.length > 0) {
      found.push({ type: 'warning', msg: `${missingCal.length} booking${missingCal.length > 1 ? 's' : ''} missing calendar event` });
    }

    // Incomplete onboarding
    const { data: incomplete } = await supabase
      .from('students')
      .select('wa_id')
      .or('name.is.null,has_own_car.is.null');
    if (incomplete?.length > 0) {
      found.push({ type: 'warning', msg: `${incomplete.length} student${incomplete.length > 1 ? 's' : ''} with incomplete onboarding` });
    }

    // Students with no instructor
    const { data: orphaned } = await supabase
      .from('students')
      .select('wa_id')
      .is('instructor_key', null);
    if (orphaned?.length > 0) {
      found.push({ type: 'warning', msg: `${orphaned.length} student${orphaned.length > 1 ? 's' : ''} with no instructor linked` });
    }

    setAlerts(found);
    setLastRefresh(new Date());
  }, []);

  // ── Auto-refresh every 60 seconds ────────────────────────────────────────
  useEffect(() => {
    loadAlerts();
    const interval = setInterval(loadAlerts, 60000);
    return () => clearInterval(interval);
  }, [loadAlerts]);

  const criticalAlerts = alerts.filter(a => a.type === 'critical');
  const alertCount     = alerts.length;

  async function handleSignOut() {
    await supabase.auth.signOut();
  }

  const NAV = [
    { id: 'today',  label: '📊 Today' },
    { id: 'growth', label: '📈 Growth' },
    {
      id: 'system',
      label: (
        <span style={{ position: 'relative' }}>
          ⚙️ System
          {alertCount > 0 && (
            <span style={{
              position: 'absolute', top: -6, right: -14,
              background: criticalAlerts.length > 0 ? '#ff4444' : '#ff8800',
              color: '#fff', borderRadius: 10, fontSize: 10,
              fontWeight: 700, padding: '1px 5px', lineHeight: '14px'
            }}>
              {alertCount}
            </span>
          )}
        </span>
      )
    }
  ];

  return (
    <div style={{
      minHeight: '100vh', background: '#0f0f0f',
      fontFamily: 'system-ui, sans-serif', color: '#fff'
    }}>
      {/* Header */}
      <div style={{
        background: '#1a1a1a', borderBottom: '1px solid #2a2a2a',
        padding: '16px 20px', display: 'flex',
        justifyContent: 'space-between', alignItems: 'center',
        position: 'sticky', top: 0, zIndex: 100
      }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>Visionry</div>
          <div style={{ fontSize: 11, color: '#555', marginTop: 2 }}>
            Updated {lastRefresh.toLocaleTimeString('en-IE', { hour: '2-digit', minute: '2-digit', hour12: false })}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            onClick={loadAlerts}
            style={{
              background: 'none', border: '1px solid #2a2a2a', borderRadius: 8,
              color: '#888', fontSize: 13, padding: '6px 12px', cursor: 'pointer'
            }}
          >
            ↻ Refresh
          </button>
          <button
            onClick={handleSignOut}
            style={{
              background: 'none', border: '1px solid #2a2a2a', borderRadius: 8,
              color: '#888', fontSize: 13, padding: '6px 12px', cursor: 'pointer'
            }}
          >
            Sign out
          </button>
        </div>
      </div>

      {/* Critical alert banner */}
      {criticalAlerts.length > 0 && (
        <div style={{
          background: '#2a0000', borderBottom: '1px solid #ff4444',
          padding: '10px 20px', display: 'flex', alignItems: 'center', gap: 8
        }}>
          <span style={{ color: '#ff4444', fontWeight: 700, fontSize: 14 }}>🚨</span>
          <span style={{ color: '#ff6666', fontSize: 13 }}>
            {criticalAlerts.map(a => a.msg).join(' · ')}
          </span>
        </div>
      )}

      {/* Nav tabs */}
      <div style={{
        background: '#1a1a1a', borderBottom: '1px solid #2a2a2a',
        display: 'flex', padding: '0 20px'
      }}>
        {NAV.map(n => (
          <button
            key={n.id}
            onClick={() => setActive(n.id)}
            style={{
              background: 'none', border: 'none',
              borderBottom: active === n.id ? '2px solid #6c47ff' : '2px solid transparent',
              color: active === n.id ? '#fff' : '#888', fontSize: 14,
              padding: '14px 16px', cursor: 'pointer',
              fontWeight: active === n.id ? 600 : 400,
              position: 'relative'
            }}
          >
            {n.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={{ padding: '20px', maxWidth: 800, margin: '0 auto' }}>
        {active === 'today'  && <Today lastRefresh={lastRefresh} />}
        {active === 'growth' && <Growth lastRefresh={lastRefresh} />}
        {active === 'system' && <System alerts={alerts} lastRefresh={lastRefresh} />}
      </div>
    </div>
  );
}